import { useEffect, useMemo, useState } from 'react';
import { CheckOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation, Col, Row } from '@packages/utils';
import CardContent from 'components/Base/Layout/Content/CardContent';
import Button from 'components/Base/Button/Button';
import { TabBodyWrapper } from 'components/Base/Tabs';
import { Table } from 'components/Base/Table';
import useTable from 'components/Base/Table/hooks/useTable';
import { TColumnType } from 'components/Base/Table/Table.types';
import { TInputOutputRow, TIoSourceType } from 'data/types/io.types';
import useGetInputOutputColumnsData from 'components/Shared/InputOutputTable/InputOutputTable.data';
import OutputActions from 'components/Shared/InputOutputTable/Actions/OutputActions';
import { EIconNames, Icon } from 'components/Base/Icon';
import { useTheme } from 'styled-components';
import { Modal } from 'components/Base/Modal';
import ConfirmModalBody from 'components/Shared/ConfirmModalBody';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import {
  useGetIoTransactionsQuery,
  useDeleteInputOutputMutation,
} from 'redux/apiSlice/io.slice';
import EditInputOutputModal from 'components/Shared/EditInputOutputModal';
import { get, isEqual } from 'lodash';

type TProps = {
  id: string;
  hostId?: string;
  locationId?: string;
  isVirtual?: boolean;
  sourceType?: TIoSourceType;
  isWorkshopOrEvent?: boolean;
};

const OutputTab = ({
  id,
  hostId,
  locationId,
  isVirtual = false,
  sourceType = 'WORKSHOP',
  isWorkshopOrEvent = true,
}: TProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [rows, setRows] = useState<TInputOutputRow[]>([]);
  const [editingRecord, setEditingRecord] = useState<TInputOutputRow | null>(
    null,
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState<TInputOutputRow | null>(
    null,
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { showToastNotification } = useShowToastNotification();
  const [deleteInputOutput, { isLoading: isDeleting }] =
    useDeleteInputOutputMutation();

  const storageKey = `${sourceType.toLowerCase()}OutputPrototype_${id}`;

  const { getColumns, updateColumnsWithFilters, categoryFilters } =
    useGetInputOutputColumnsData(sourceType);

  const defaultColumns = getColumns;

  const {
    page,
    pageSize,
    sortColumn,
    selectedFilters,
    setPageSize,
    hasSelectedFilter,
    onFiltersReset,
    columns,
    setColumns,
    handleTableChange,
    selectedRows,
    setSelectedRows,
  } = useTable<TInputOutputRow>({
    storagePath: storageKey,
    defaultColumns,
    defaultPageSize: '10',
  });

  const { data, isFetching } = useGetIoTransactionsQuery({
    sourceId: id,
    sourceType,
    page,
    pageSize,
    sortColumn,
    filters: selectedFilters,
  });

  useEffect(() => {
    const items = (data?.content || []) as TInputOutputRow[];
    setRows(items);

    setColumns(prev => {
      const updated = updateColumnsWithFilters(prev, data, items);
      return isEqual(prev, updated) ? prev : updated;
    });
  }, [data, categoryFilters]);

  const handleEdit = (record: TInputOutputRow) => {
    setEditingRecord(record);
    setIsEditModalOpen(true);
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
    setEditingRecord(null);
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    setEditingRecord(null);
  };

  const handleCreateCancel = () => {
    setIsCreateModalOpen(false);
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
  };

  const handleDeleteClick = (record: TInputOutputRow) => {
    setDeletingRecord(record);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRecord) return;
    try {
      await deleteInputOutput({
        resourceId: deletingRecord.id,
      }).unwrap();
      showToastNotification({
        message: 'Input/Output deleted successfully',
        type: ENotificationType.SUCCESS,
      });
    } catch (err) {
      showToastNotification({
        message: get(err, 'data.message'),
        type: ENotificationType.ERROR,
      });
    }
    setIsDeleteModalOpen(false);
    setDeletingRecord(null);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeletingRecord(null);
  };

  const columnsWithActions: TColumnType<TInputOutputRow>[] = useMemo(() => {
    const inkindColumn: TColumnType<TInputOutputRow> = {
      title: 'In-Kind',
      key: 'isInkindDonation',
      dataIndex: 'isInkindDonation',
      width: 130,
      filters: [{ text: 'In-Kind Only', value: 'true' }],
      filteredValue: selectedFilters.isInkindDonation || null,
      render: (_: unknown, record: TInputOutputRow) => {
        if (!record.isInkindDonation) return '-';
        const value = record.inkindDollarValue ?? 0;
        const formatted = `$${value.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`;
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CheckOutlined style={{ color: '#52c41a', fontSize: 12 }} />
            {formatted}
          </span>
        );
      },
    };

    const actionsColumn: TColumnType<TInputOutputRow> = {
      title: 'Actions',
      key: 'actions',
      dataIndex: 'actions',
      width: 80,
      fixed: 'right',
      onCell: () => ({
        style: { minWidth: '80px' },
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
        },
      }),
      render: (_: unknown, record: TInputOutputRow) => {
        if (!record.canEdit) return null;
        return (
          <Row justify="center" gutter={[12, 12]} wrap={false}>
            <Col onClick={() => handleEdit(record)}>
              <Icon
                icon={EIconNames.EDIT_OUTLINED}
                color={theme.colorBlackIconColor}
                className="pointer"
                tooltip={{ title: 'Edit' }}
              />
            </Col>
            <Col onClick={() => handleDeleteClick(record)}>
              <Icon
                icon={EIconNames.DELETE_OUTLINED}
                color={theme.colorBlackIconColor}
                className="pointer"
                tooltip={{ title: 'Delete' }}
              />
            </Col>
          </Row>
        );
      },
    };
    const columnsWithFilters = columns.map(col => ({
      ...col,
      filteredValue: selectedFilters[col.key as string] || null,
    }));
    return [...columnsWithFilters, inkindColumn, actionsColumn];
  }, [columns, selectedFilters]);

  const additionalActions = useMemo(
    () => [
      <OutputActions
        key="actions"
        selectedRowsKeys={selectedRows.selectedRowsKeys}
        sourceId={id}
        sourceType={sourceType}
        storageKey={storageKey}
        selectedFilters={selectedFilters}
        sortColumn={sortColumn}
      />,
      <Button
        key="add"
        icon={<PlusOutlined />}
        onClick={() => setIsCreateModalOpen(true)}
        style={{ fontWeight: 700 }}
        data-cy="btn_add_inputOutput"
      >
        Add Input/Output
      </Button>,
    ],
    [
      selectedRows.selectedRowsKeys,
      id,
      sourceType,
      storageKey,
      selectedFilters,
      sortColumn,
    ],
  );

  return (
    <CardContent tabContent contentPadding="8px 0px">
      <TabBodyWrapper>
        <Table<TInputOutputRow>
          tableTitle={t(
            'descriptions.admin-portal.input-output.TITLE',
            'Inputs/Outputs',
          )}
          onFiltersReset={onFiltersReset}
          hasFilters={hasSelectedFilter}
          columns={columnsWithActions}
          headerActions={additionalActions}
          dataSource={rows}
          loading={isFetching}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalDataCount={data?.totalElements || 0}
          onChange={handleTableChange}
          rowKey={record => record.id}
          currentPage={page}
          storagePath={storageKey}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />
      </TabBodyWrapper>

      {/* Edit modal */}
      {editingRecord && (
        <EditInputOutputModal
          mode="edit"
          open={isEditModalOpen}
          record={editingRecord}
          sourceType={sourceType}
          sourceId={id}
          hostId={hostId}
          locationId={locationId}
          isWorkshopOrEvent={isWorkshopOrEvent}
          isVirtual={isVirtual}
          hasLocation={!!locationId}
          onSuccess={handleEditSuccess}
          onCancel={handleEditCancel}
        />
      )}

      {/* Create modal */}
      <EditInputOutputModal
        mode="create"
        open={isCreateModalOpen}
        sourceType={sourceType}
        sourceId={id}
        hostId={hostId}
        locationId={locationId}
        isWorkshopOrEvent={isWorkshopOrEvent}
        isVirtual={isVirtual}
        hasLocation={!!locationId}
        onSuccess={handleCreateSuccess}
        onCancel={handleCreateCancel}
      />

      {/* Delete confirmation modal */}
      <Modal
        open={isDeleteModalOpen}
        title="Delete Input/Output"
        cancelText="Cancel"
        okText="Delete"
        onCancel={handleDeleteCancel}
        onOk={handleDeleteConfirm}
        width={350}
        confirmLoading={isDeleting}
      >
        <ConfirmModalBody
          title="Are you sure?"
          description="This action will permanently delete this input/output record. This cannot be undone."
        />
      </Modal>
    </CardContent>
  );
};

export default OutputTab;
